import {
  ElementRef,
  AfterViewInit,
  OnInit,
  OnDestroy,
  Component,
  HostListener,
  ViewChild,
  inject,
  Input,
  computed, effect,
} from '@angular/core';
import { RentalPoint } from 'src/app/models/rental-point.model';
import { GroupResult, process } from '@progress/kendo-data-query';
import { MapComponent } from 'src/app/pages/form/rental-points-selection/map/map.component';
import { LabelComponent } from '@progress/kendo-angular-label';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent, DatePickerCustomMessagesComponent } from '@progress/kendo-angular-dateinputs';
import {
  DiscountStateComponent
} from 'src/app/pages/form/rental-points-selection/discount-state/discount-state.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ErrorComponent, FormFieldComponent } from '@progress/kendo-angular-inputs';
import { LanguageService } from 'src/app/services/language.service';
import { ControlNamePipe } from 'src/app/pipes/control-name.pipe';
import { Icons } from 'src/app/icons';
import { logger, Utils } from 'src/app/utils';
import { ScheduleComponent } from 'src/app/pages/form/schedule/schedule.component';
import { DISCOUNTS } from 'src/app/app.constants';
import { SpacerComponent } from 'src/app/ui/atoms/spacer/spacer.component';
import { DialogContainerDirective, DialogService } from "@progress/kendo-angular-dialog";
import { TestDataService } from 'src/app/services/testData.service';
import { Subscription } from 'rxjs';
import { FormService } from 'src/app/services/form.service';
import { isEqualDate } from '@progress/kendo-date-math';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-rental-points-selection',
    imports: [
        DatePickerComponent,
        TranslatePipe,
        DatePickerCustomMessagesComponent,
        ReactiveFormsModule,
        FormFieldComponent,
        ErrorComponent,
        FormsModule,
        ControlNamePipe,
        LabelComponent,
        DiscountStateComponent,
        ScheduleComponent,
        SpacerComponent,
        MapComponent,
        DialogContainerDirective,
        NgClass,
    ],
    templateUrl: './rental-points-selection.component.html',
    styleUrl: './rental-points-selection.component.scss'
})
export class RentalPointsSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
  private testDataService = inject(TestDataService);
  private testDataSubscription: Subscription;

  @ViewChild('mapContainer', {static: true}) mapContainer: ElementRef;

  readonly icons = Icons;

  private languageService = inject(LanguageService);
  private dialogService = inject(DialogService);
  public formService = inject(FormService);

  lang = computed(() => this.languageService.currentLanguage().toLowerCase());
  startRentalPoints = this.formService.startRentalPoints;
  endRentalPoints = this.formService.endRentalPoints;

  @Input({required: true}) formId: string;

  mapHeight: number;

  filteredStartRentalPoints: GroupResult[];
  filteredEndRentalPoints: GroupResult[];

  disabledStartDates: ((date: Date) => boolean);
  disabledEndDates: ((date: Date) => boolean);

  formGroup: FormGroup;
  tomorrow: Date;

  readonly format = 'dd/MMM/yyyy';

  private initialData: any = {};

  smallWidth = false;

  private dateValidator(getDisabledDates: () => ((date: Date) => boolean)): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const date = control.value;
      if (!date) return null;
      const disabledFn = getDisabledDates();
      if (disabledFn && disabledFn(date)) {
        return { disabledDate: true };
      }
      return null;
    };
  }

  constructor(private translateService: TranslateService) {
    const tmr = new Date();
    tmr.setDate(tmr.getDate() + 1);
    tmr.setHours(0, 0, 0, 0);
    this.tomorrow = tmr;
    this.formGroup = new FormGroup<any>({
      startRental: new FormControl(null, Validators.required),
      endRental: new FormControl(null, Validators.required),
      from: new FormControl(
        {value: null, disabled: true},
        [
          Validators.required,
          this.dateValidator(() => this.disabledStartDates)
        ]
      ),
      to: new FormControl(
        {value: null, disabled: true},
        [
          Validators.required,
          this.dateValidator(() => this.disabledEndDates)
        ]
      ),
    });
    effect(() => {
      this.filteredStartRentalPoints = this.groupByVillage(this.startRentalPoints());
      this.filteredEndRentalPoints = this.groupByVillage(this.endRentalPoints());
      this.setMapHeight();
    });
  }

  ngOnInit() {
    this.smallWidth = window.innerWidth < 482;
    if (this.formService.rentalPointsSignal().length === 0) {
      this.formService.fetchRentalPoints(this.formService.params).subscribe({
        next: (rentalPoints: any) => this.formService.rentalPointsSignal.update(() => {
          return rentalPoints.filter((r: any) => {
            const isValidLongitude = r.longitude && !isNaN(r.longitude);
            const isValidLatitude = r.latitude && !isNaN(r.latitude);
            return isValidLongitude && isValidLatitude;
          });
        }),
        error: error => console.error('Error fetching rental points:', error)
      });
    }

    const {  startRental, endRental, from, to } = this.formService.form();
    this.formGroup.get('startRental')?.setValue(startRental?.accountId);
    this.formGroup.get('endRental')?.setValue(endRental?.accountId);
    if (from) {
      this.startRentalSelectionChange({target: {value: startRental?.accountId}}, false);
      this.formGroup.get('from')?.setValue(from);
      this.initialData = {
        startRental: startRental,
        from: from,
      }
    }
    if (to) {
      this.endRentalSelectionChange({target: {value: endRental?.accountId}});
      this.formGroup.get('to')?.setValue(to);
      this.initialData = {
        ...this.initialData,
        endRental: endRental,
        to: to,
      }
    }

    this.formService.accessoriesViewed.set(false);
    this.formGroup.valueChanges.subscribe((values) => {
      this.buildRentalPointsSelectionData();
      if (values?.from || values?.to) this.checkDaysDiscount();
    });
    this.initTestDataObserver();
  }

  private initTestDataObserver() {
    this.testDataSubscription = this.testDataService.fillDataObserver.subscribe(() => {
      if (document.getElementById(this.formId)) {
        const randomStartRental = Utils.getRandomElement(this.startRentalPoints());
        this.formGroup.get('startRental')?.setValue(randomStartRental.accountId);
        this.startRentalSelectionChange({target: {value: randomStartRental}}, this.testDataService.sameRentalPoint);
        const firstAvailableStartDate = this.getFirstAvailableDate(this.disabledStartDates);
        if (firstAvailableStartDate) this.formGroup.get('from')?.setValue(firstAvailableStartDate);

        if (!this.testDataService.sameRentalPoint) {
          const randomEndRental = Utils.getRandomElement(this.endRentalPoints());
          this.formGroup.get('endRental')?.setValue(randomEndRental);
          this.endRentalSelectionChange({target: {value: randomEndRental}});
          const firstAvailableEndDate = this.getFirstAvailableDate(this.disabledEndDates);
          if (firstAvailableEndDate) this.formGroup.get('to')?.setValue(firstAvailableEndDate);
        } else {
          this.formGroup.get('to')?.setValue(firstAvailableStartDate);
        }

        this.formGroup.markAllAsTouched();
      }
    });
  }

  private getFirstAvailableDate(disabledDates: (date: Date) => boolean): Date | null {
    for (let i = 0; i < 365; i++) {
      const date = new Date(this.tomorrow);
      date.setDate(this.tomorrow.getDate() + i);
      if (Array.isArray(disabledDates)) {
        if (!disabledDates.some((d: any) => isEqualDate(d, date))) return date;
      } else if (typeof disabledDates === 'function') {
        if (!disabledDates(date)) return date;
      }
    }
    return null;
  }

  private checkDaysDiscount() {
    const firstDayDiscount = Number((DISCOUNTS.filter(d => {
      return d.range.includes('-') && d.range.split('-').length === 2 && !isNaN(d.range.split('-')[0] as any);
    }).map(d => parseInt(d.range.split('-')[0]))[0])) - 1;

    const nextDaysDiscount = DISCOUNTS
      .filter(d => {
        return d.range.includes('-') && d.range.split('-').length === 2 && !isNaN(d.range.split('-')[1] as any);
      })
      .map(d => parseInt(d.range.split('-')[1]));

    const proposeDiscountForDurationDays = [firstDayDiscount, ...nextDaysDiscount];

    const durationInDays = this.formService.form().durationInDays;
    if (durationInDays) {
      if (proposeDiscountForDurationDays.includes(durationInDays)) {
        const dialog = this.dialogService.open({
          content: this.translateService.instant('rentalPointsSelection.daysDiscount'),
          actions: [
            {text: this.translateService.instant('no'), fillMode: 'outline', themeColor: 'primary', value: false},
            {text: this.translateService.instant('yes'), themeColor: 'primary', value: true}
          ],
        });

        dialog.result.subscribe((result: any) => {
          if (result?.value) {
            const currentEndDate = this.formGroup.get('to')?.value as Date;
            if (currentEndDate) {
              const currentEndDatePlus1Day = new Date(currentEndDate);
              currentEndDatePlus1Day.setDate(currentEndDatePlus1Day.getDate() + 1);
              this.formGroup.get('to')?.setValue(currentEndDatePlus1Day);
            }
          }
        });
      }
    }
  }

  ngAfterViewInit() {
    this.setMapHeight();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.smallWidth = window.innerWidth < 482;
    if (!this.smallWidth) this.setMapHeight();
  }

  private setMapHeight() {
    setTimeout(() => {
      if (this.mapContainer && document.contains(this.mapContainer.nativeElement)) {
        this.mapContainer.nativeElement.style.flexGrow = '1';
        this.mapHeight = this.mapContainer.nativeElement.offsetHeight - 80; // schedule height
        this.mapContainer.nativeElement.style.flexGrow = 'unset';
      } else {
        logger.w('No map container found');
      }
    }, 300);
  }

  private filterRentalPoints(value: any, rentalPoints: RentalPoint[]): GroupResult[] {
    if (!value || value.length < 2) {
      return this.groupByVillage(rentalPoints);
    }
    const v = value?.toLowerCase();
    const filteredRentalPoints = rentalPoints.filter(r =>
      r.businessName.toLowerCase().includes(v) ||
      (r.city && r.city.toLowerCase().includes(v)) ||
      (r.village && r.village.toLowerCase().includes(v)) ||
      (r.postalCode && r.postalCode.toLowerCase().includes(v) ||
      (r.translations && Object.values(r.translations).some(t=> {
          return t.toLowerCase().includes(v);
        })
      )
    ));
    return this.groupByVillage(filteredRentalPoints);
  }

  filterStartRentalPoints(value: any) {
    this.filteredStartRentalPoints = this.filterRentalPoints(value, this.startRentalPoints());
  }

  filterEndRentalPoints(value: any) {
    this.filteredEndRentalPoints = this.filterRentalPoints(value, this.endRentalPoints());
  }

  private groupByVillage(rentalPoints: RentalPoint[]) {
    return process(rentalPoints, {
      group: [{field: 'city', dir: 'asc'}]
    }).data as GroupResult[];
  }

  startRentalSelectionChange(event?: any, updateEndRental = true) {
    const rentalPoint = this.startRentalPoints().find(r => r.accountId === Number(event?.target?.value));
    this.disabledStartDates = this.getDisabledDates(rentalPoint);

    if (rentalPoint?.schedule) {
      this.formGroup.get('from')?.enable();
      setTimeout(() => {
        this.formGroup.get('from')?.updateValueAndValidity();
        if (this.formGroup.get('from')?.value) {
          this.formGroup.get('from')?.markAsTouched();
        }
      }, 100);
    } else {
      this.formGroup.get('from')?.disable();
      this.formGroup.get('from')?.reset();
    }
    if (!rentalPoint) this.formGroup.get('from')?.reset();

    if (updateEndRental && rentalPoint) {
      this.formGroup.get('endRental')?.setValue(rentalPoint.accountId);
      this.endRentalSelectionChange({target: {value: rentalPoint.accountId}});
    }
  }

  endRentalSelectionChange(event?: any) {
    const rentalPoint = this.endRentalPoints().find(r => r.accountId === Number(event?.target?.value));
    this.disabledEndDates = this.getDisabledDates(rentalPoint);

    if (rentalPoint?.schedule) {
      this.formGroup.get('to')?.enable();
      setTimeout(() => {
        this.formGroup.get('to')?.updateValueAndValidity();
        if (this.formGroup.get('to')?.value) {
          this.formGroup.get('to')?.markAsTouched();
        }
      }, 100);
    } else {
      this.formGroup.get('to')?.disable();
      this.formGroup.get('to')?.reset();
    }
    if (!rentalPoint) this.formGroup.get('to')?.reset();
  }

  private getDisabledDates(rentalPoint?: RentalPoint): (d: Date) => boolean {
    const schedule = rentalPoint?.schedule;
    if (!schedule) return () => true; // If no schedule, disable all dates

    const scheduleExceptions = schedule.scheduleExceptions;

    // If there are no exceptions
    if (!scheduleExceptions || scheduleExceptions.length === 0) {
      // and no opening days
      if (!schedule.openingDays || schedule.openingDays.length === 0) {
        return () => true; // disable all days
      }
      // otherwise, disable day if it is not included in default openingDays
      return (date: Date) => !schedule.openingDays.includes(date.getDay());
    }

    // If there are exceptions
    return (date: Date): boolean => {
      // Filter exceptions that contains the date
      const matchingExceptions = scheduleExceptions.filter(ex => {
        if (isEqualDate(date, ex.startDay) || isEqualDate(date, ex.endDay)) return true;
        if (date < ex.startDay) return false;
        return !(date > ex.endDay);
      });

      // If there are no matching exceptions
      if (matchingExceptions.length === 0) {
        // disable day if it is not included in default openingDays
        return !schedule.openingDays.includes(date.getDay());
      }

      // If there is at least one exception that closes the entire day
      if (matchingExceptions.some(ex => ex.closingException)) {
        return true; // disable the day because closingException has priority
      }

      // Otherwise, merge the opening days of all exceptions that coincide with that date
      const unionOpenDays = new Set<number>(); // avoiding duplicates
      for (const ex of matchingExceptions) {
        for (const day of ex.openingDays) {
          unionOpenDays.add(day);
        }
      }

      // If the union is empty, it means the date is closed
      if (unionOpenDays.size === 0) return true;

      // Otherwise, disable day if it is not included in the union
      return !unionOpenDays.has(date.getDay());
    };
  }

  onStartDateChange(date?: Date) {
    const startRental = this.formGroup.get('startRental')?.value;
    const endRental =  this.formGroup.get('endRental')?.value;
    if (startRental && endRental) {
      const from = this.formGroup.get('from')?.value || date;
      const to = this.formGroup.get('to')?.value;
      if (Number(startRental) === Number(endRental)) {
        if (from && !to) this.formGroup.get('to')?.setValue(from);
      } else {
        if (from && !to) {
          if (!this.disabledEndDates || !this.disabledEndDates(from)) {
            this.formGroup.get('to')?.setValue(from);
          }
        }
      }
    }
  }

  private buildRentalPointsSelectionData() {
    const values: any = this.formGroup.getRawValue();
    this.formService.update({
      ...this.formService.form(),
      from: values.from,
      to: values.to,
      startRental: this.startRentalPoints().find(r => r.accountId === Number(values.startRental)),
      endRental: this.endRentalPoints().find(r => r.accountId === Number(values.endRental)),
      durationInDays: (values.from && values.to) ? Utils.calcDaysDifference(values.from, values.to) : undefined,
      previousSameRental: this.isSameFromInitialData()
    });
  }

  private isSameFromInitialData(): boolean {
    const previous = this.initialData;
    const data = this.formGroup.getRawValue();
    return !!previous?.startRental && !!data?.startRental
      && !!previous?.endRental && !!data?.endRental
      && !!previous?.from && !!previous?.to && !!data.from && !!data.to
      && previous.startRental.accountId === data.startRental
      && previous.endRental.accountId === data.endRental
      && isEqualDate(previous.from, data.from) && isEqualDate(previous.to, data.to);
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.buildRentalPointsSelectionData();
    const same = this.isSameFromInitialData();
    if (!same) {
      this.formService.update({
        ...this.formService.form(),
        bikes: [],
        accessories: [],
        previousBikes: [],
      });
      this.formService.bikes.update(() => []);
      this.formService.accessories.update(() => []);
    }
  }

  ngOnDestroy() {
    this.testDataSubscription?.unsubscribe();
  }

}
